import React, { useEffect, useState } from "react";

import {
  BriefcaseBusiness,
  MapPin,
  Users,
  Clock3,
  RefreshCw,
  Eye,
  Pencil,
  X,
  CalendarDays,
  IndianRupee,
  FileText,
  GraduationCap,
  Building2,
  Save,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

import jobService from "../services/JobService";

function AllJob() {

  // =====================================================
  // STATE
  // =====================================================

  const [jobs, setJobs] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState("");

  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  // View drawer
  const [selectedJob, setSelectedJob] = useState(null);

  const [showViewDrawer, setShowViewDrawer] = useState(false);

  // Edit drawer
  const [showEditDrawer, setShowEditDrawer] = useState(false);

  const [editJob, setEditJob] = useState(null);

  const [savingJob, setSavingJob] = useState(false);

  const [saveMessage, setSaveMessage] = useState("");

  const [saveError, setSaveError] = useState("");


  // =====================================================
  // GET ALL JOBS
  // =====================================================

  const fetchJobs = async () => {

    try {

      setLoading(true);

      setError("");

      const response =
        await jobService.getAllJobs();

      console.log(
        "ALL JOBS API RESPONSE:",
        response
      );


      if (Array.isArray(response)) {

        setJobs(response);

      } else if (Array.isArray(response?.data)) {

        setJobs(response.data);

      } else if (Array.isArray(response?.content)) {

        setJobs(response.content);

      } else {

        setJobs([]);

      }

    } catch (err) {

      console.error(
        "GET JOBS ERROR:",
        err
      );

      setError(
        err?.message ||
        "Unable to load jobs."
      );

    } finally {

      setLoading(false);

    }

  };


  // =====================================================
  // LOAD JOBS
  // =====================================================

  useEffect(() => {

    fetchJobs();

  }, []);


  // =====================================================
  // VIEW JOB
  // =====================================================

  const handleViewJob = (job) => {

    setSelectedJob(job);

    setShowViewDrawer(true);

  };


  // =====================================================
  // CLOSE VIEW
  // =====================================================

  const closeViewDrawer = () => {

    setShowViewDrawer(false);

    setTimeout(() => {
      setSelectedJob(null);
    }, 250);

  };


  // =====================================================
  // OPEN EDIT
  // =====================================================

  const handleEditJob = (job) => {

    setSaveMessage("");

    setSaveError("");

    setEditJob({
      ...job
    });

    setShowEditDrawer(true);

  };


  // =====================================================
  // CLOSE EDIT
  // =====================================================

  const closeEditDrawer = () => {

    if (savingJob) {
      return;
    }

    setShowEditDrawer(false);

    setTimeout(() => {
      setEditJob(null);
    }, 250);

  };


  // =====================================================
  // EDIT INPUT CHANGE
  // =====================================================

  const handleEditChange = (field, value) => {

    setEditJob((prev) => ({
      ...prev,
      [field]: value
    }));

  };


  // =====================================================
  // UPDATE JOB
  // =====================================================

  const handleUpdateJob = async () => {

    if (!editJob?.jobId) {
      return;
    }

    try {

      setSavingJob(true);

      setSaveMessage("");

      setSaveError("");


      console.log(
        "UPDATING JOB:",
        editJob
      );


      /*
       * IMPORTANT:
       *
       * JobService me updateJob(jobId, data)
       * method hona chahiye.
       *
       * Agar aapke backend ka update endpoint
       * PATCH/PUT hai to JobService me uske
       * according method rakhein.
       */

      const updatedJob =
        await jobService.updateJob(
          editJob.jobId,
          editJob
        );


      console.log(
        "UPDATE JOB RESPONSE:",
        updatedJob
      );


      // Backend response normalize
      const updatedData =
        updatedJob?.data ||
        updatedJob ||
        editJob;


      // Update UI immediately
      setJobs((prevJobs) =>

        prevJobs.map((job) => {

          if (
            Number(job.jobId) !==
            Number(editJob.jobId)
          ) {
            return job;
          }

          return {
            ...job,
            ...updatedData
          };

        })

      );


      // Selected job bhi update
      setSelectedJob((prev) => {

        if (
          !prev ||
          Number(prev.jobId) !==
          Number(editJob.jobId)
        ) {
          return prev;
        }

        return {
          ...prev,
          ...updatedData
        };

      });


      setSaveMessage(
        "Job updated successfully."
      );


      // Backend se latest data
      await fetchJobs();


      setTimeout(() => {

        setShowEditDrawer(false);

        setEditJob(null);

      }, 700);


    } catch (err) {

      console.error(
        "UPDATE JOB ERROR:",
        err
      );


      setSaveError(
        err?.response?.data?.message ||
        err?.message ||
        "Unable to update job."
      );

    } finally {

      setSavingJob(false);

    }

  };


  // =====================================================
  // UPDATE JOB STATUS
  // =====================================================

  const handleStatusChange = async (
    jobId,
    newStatus
  ) => {

    if (!jobId || !newStatus) {
      return;
    }


    const currentJob =
      jobs.find(
        (job) =>
          Number(job.jobId) ===
          Number(jobId)
      );


    const previousStatus =
      currentJob?.status ||
      "PENDING";


    try {

      setUpdatingStatusId(jobId);


      const updatedJob =
        await jobService.updateJobStatus(
          jobId,
          newStatus
        );


      const updatedStatus =
        updatedJob?.status ||
        updatedJob?.data?.status ||
        newStatus;


      setJobs((prevJobs) =>

        prevJobs.map((job) => {

          if (
            Number(job.jobId) !==
            Number(jobId)
          ) {
            return job;
          }

          return {
            ...job,
            status: updatedStatus
          };

        })

      );


      // Selected drawer job update
      setSelectedJob((prev) => {

        if (
          !prev ||
          Number(prev.jobId) !==
          Number(jobId)
        ) {
          return prev;
        }

        return {
          ...prev,
          status: updatedStatus
        };

      });


    } catch (err) {

      console.error(
        "UPDATE JOB STATUS ERROR:",
        err
      );


      setJobs((prevJobs) =>

        prevJobs.map((job) => {

          if (
            Number(job.jobId) !==
            Number(jobId)
          ) {
            return job;
          }

          return {
            ...job,
            status: previousStatus
          };

        })

      );


      alert(
        err?.message ||
        "Unable to update job status."
      );

    } finally {

      setUpdatingStatusId(null);

    }

  };


  // =====================================================
  // STATUS CLASS
  // =====================================================

  const getStatusClass = (status) => {

    const normalizedStatus =
      status
        ?.toString()
        .toLowerCase() ||
      "pending";

    return `job-status ${normalizedStatus}`;

  };


  // =====================================================
  // FORMAT VALUE
  // =====================================================

  const displayValue = (value) => {

    if (
      value === null ||
      value === undefined ||
      value === ""
    ) {
      return "-";
    }

    return value;

  };


  // =====================================================
  // APPLICATION COUNT
  // =====================================================

  const getApplicationCount = (job) => {

    return (
      job?.applicationCount ??
      job?.applicationsCount ??
      job?.applications?.length ??
      0
    );

  };


  // =====================================================
  // PAGE
  // =====================================================

  return (

    <Page title="My Jobs">

      {/* =================================================
          PAGE HEADER
      ================================================= */}

      <div className="jobs-header">

        <div>

          <h2>
            My Jobs
          </h2>

          <p>
            Manage all jobs posted by your account
          </p>

        </div>


        <button
          className="refresh-btn"
          onClick={fetchJobs}
          disabled={loading}
        >

          <RefreshCw
            size={16}
            className={
              loading
                ? "refresh-spin"
                : ""
            }
          />

          Refresh

        </button>

      </div>


      {/* =================================================
          ERROR
      ================================================= */}

      {error && (

        <div className="jobs-error">

          <span>
            {error}
          </span>

          <button
            onClick={fetchJobs}
          >
            Try Again
          </button>

        </div>

      )}


      {/* =================================================
          LOADING
      ================================================= */}

      {loading ? (

        <div className="jobs-loading">

          <RefreshCw
            size={24}
            className="refresh-spin"
          />

          <p>
            Loading jobs...
          </p>

        </div>

      ) : jobs.length === 0 ? (

        <div className="jobs-empty">

          <BriefcaseBusiness
            size={45}
          />

          <h3>
            No Jobs Found
          </h3>

          <p>
            You haven't posted any jobs yet.
          </p>

        </div>

      ) : (

        /* =================================================
           JOB TABLE
        ================================================= */

        <div className="jobs-table-wrapper">

          <table className="jobs-table">

            <thead>

              <tr>

                <th>
                  Job
                </th>

                <th>
                  Technology / Skills
                </th>

                <th>
                  Location
                </th>

                <th>
                  Employment
                </th>

                <th>
                  Experience
                </th>

                <th>
                  Vacancy
                </th>

                <th>
                  Status
                </th>

                <th>
                  Applications
                </th>

                <th>
                  Actions
                </th>

              </tr>

            </thead>


            <tbody>

              {jobs.map((job) => (

                <tr
                  key={job.jobId}
                >

                  {/* JOB */}

                  <td>

                    <div className="job-title-cell">

                      <div className="job-icon">

                        <BriefcaseBusiness
                          size={17}
                        />

                      </div>


                      <div>

                        <strong>
                          {job.jobTitle ||
                            "Untitled Job"}
                        </strong>

                        <small>
                          Job ID:{" "}
                          {job.jobId || "-"}
                        </small>

                      </div>

                    </div>

                  </td>


                  {/* SKILLS */}

                  <td>

                    <div className="skills-cell">

                      {job.skillsRequired ||
                        job.technology ||
                        "-"}

                    </div>

                  </td>


                  {/* LOCATION */}

                  <td>

                    <div className="location-cell">

                      <MapPin
                        size={15}
                      />

                      {job.location ||
                        "-"}

                    </div>

                  </td>


                  {/* EMPLOYMENT */}

                  <td>

                    {job.employeeType ||
                      "-"}

                  </td>


                  {/* EXPERIENCE */}

                  <td>

                    <div className="experience-cell">

                      <Clock3
                        size={14}
                      />

                      {job.experienceRequired ||
                        "-"}

                    </div>

                  </td>


                  {/* VACANCY */}

                  <td>

                    <div className="vacancy-cell">

                      <Users
                        size={15}
                      />

                      {job.vacancy ?? 0}

                    </div>

                  </td>


                  {/* STATUS */}

                  <td>

                    <select
                      value={
                        job.status ||
                        "PENDING"
                      }

                      onChange={(e) =>
                        handleStatusChange(
                          job.jobId,
                          e.target.value
                        )
                      }

                      disabled={
                        updatingStatusId ===
                        job.jobId
                      }

                      className={getStatusClass(
                        job.status
                      )}
                    >

                      <option value="PENDING">
                        PENDING
                      </option>

                      <option value="APPROVED">
                        APPROVED
                      </option>

                      <option value="REJECTED">
                        REJECTED
                      </option>

                      <option value="CLOSED">
                        CLOSED
                      </option>

                    </select>

                  </td>


                  {/* APPLICATION COUNT */}

                  <td>

                    <span className="application-count">

                      {getApplicationCount(job)}

                    </span>

                  </td>


                  {/* ACTIONS */}

                  <td>

                    <div className="job-actions">

                      {/* VIEW */}

                      <button
                        className="job-action-btn view"
                        title="View Job Details"
                        onClick={() =>
                          handleViewJob(job)
                        }
                      >

                        <Eye
                          size={16}
                        />

                      </button>


                      {/* EDIT */}

                      <button
                        className="job-action-btn edit"
                        title="Update Job"
                        onClick={() =>
                          handleEditJob(job)
                        }
                      >

                        <Pencil
                          size={16}
                        />

                      </button>

                    </div>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      )}


      {/* =====================================================
          RIGHT SIDE VIEW DRAWER
      ===================================================== */}

      {showViewDrawer && selectedJob && (

        <div
          className="drawer-overlay"
          onClick={closeViewDrawer}
        >

          <div
            className="job-drawer"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* HEADER */}

            <div className="drawer-header">

              <div>

                <span className="drawer-label">
                  JOB DETAILS
                </span>

                <h2>
                  {displayValue(
                    selectedJob.jobTitle
                  )}
                </h2>

                <p>
                  Job ID:{" "}
                  {displayValue(
                    selectedJob.jobId
                  )}
                </p>

              </div>


              <button
                className="drawer-close"
                onClick={closeViewDrawer}
              >

                <X size={19} />

              </button>

            </div>


            {/* BODY */}

            <div className="drawer-body">

              <div className="drawer-status-row">

                <span>
                  Status
                </span>

                <select
                  value={
                    selectedJob.status ||
                    "PENDING"
                  }

                  onChange={(e) =>
                    handleStatusChange(
                      selectedJob.jobId,
                      e.target.value
                    )
                  }

                  className={getStatusClass(
                    selectedJob.status
                  )}
                >

                  <option value="PENDING">
                    PENDING
                  </option>

                  <option value="APPROVED">
                    APPROVED
                  </option>

                  <option value="REJECTED">
                    REJECTED
                  </option>

                  <option value="CLOSED">
                    CLOSED
                  </option>

                </select>

              </div>


              {/* QUICK INFO */}

              <div className="quick-info-grid">

                <InfoBox
                  icon={<MapPin size={16} />}
                  label="Location"
                  value={selectedJob.location}
                />

                <InfoBox
                  icon={<Clock3 size={16} />}
                  label="Experience"
                  value={selectedJob.experienceRequired}
                />

                <InfoBox
                  icon={<Users size={16} />}
                  label="Vacancy"
                  value={selectedJob.vacancy}
                />

                <InfoBox
                  icon={<FileText size={16} />}
                  label="Applications"
                  value={getApplicationCount(
                    selectedJob
                  )}
                />

                <InfoBox
                  icon={<Building2 size={16} />}
                  label="Employment"
                  value={selectedJob.employeeType}
                />

                <InfoBox
                  icon={<IndianRupee size={16} />}
                  label="Salary"
                  value={
                    selectedJob.salary ||
                    selectedJob.salaryRange ||
                    selectedJob.rate
                  }
                />

              </div>


              {/* DETAILS */}

              <DetailSection
                title="Job Information"
                icon={<BriefcaseBusiness size={17} />}
              >

                <DetailRow
                  label="Job Title"
                  value={selectedJob.jobTitle}
                />

                <DetailRow
                  label="Technology"
                  value={
                    selectedJob.technology
                  }
                />

                <DetailRow
                  label="Skills Required"
                  value={
                    selectedJob.skillsRequired
                  }
                />

                <DetailRow
                  label="Employment Type"
                  value={
                    selectedJob.employeeType
                  }
                />

                <DetailRow
                  label="Experience Required"
                  value={
                    selectedJob.experienceRequired
                  }
                />

                <DetailRow
                  label="Vacancy"
                  value={
                    selectedJob.vacancy
                  }
                />

              </DetailSection>


              {/* DESCRIPTION */}

              <DetailSection
                title="Job Description"
                icon={<FileText size={17} />}
              >

                <div className="description-box">

                  {displayValue(
                    selectedJob.description ||
                    selectedJob.jobDescription
                  )}

                </div>

              </DetailSection>


              {/* REQUIREMENTS */}

              <DetailSection
                title="Requirements"
                icon={<GraduationCap size={17} />}
              >

                <div className="description-box">

                  {displayValue(
                    selectedJob.requirements ||
                    selectedJob.jobRequirements
                  )}

                </div>

              </DetailSection>


              {/* ADDITIONAL DETAILS */}

              <DetailSection
                title="Additional Details"
                icon={<CalendarDays size={17} />}
              >

                <DetailRow
                  label="Start Date"
                  value={
                    selectedJob.startDate
                  }
                />

                <DetailRow
                  label="End Date"
                  value={
                    selectedJob.endDate
                  }
                />

                <DetailRow
                  label="Work Mode"
                  value={
                    selectedJob.workMode ||
                    selectedJob.mode
                  }
                />

                <DetailRow
                  label="Created At"
                  value={
                    selectedJob.createdAt
                  }
                />

                <DetailRow
                  label="Updated At"
                  value={
                    selectedJob.updatedAt
                  }
                />

              </DetailSection>


              {/* APPLICATION */}

              <div className="application-summary">

                <div className="application-summary-icon">

                  <Users size={19} />

                </div>

                <div>

                  <strong>
                    {getApplicationCount(
                      selectedJob
                    )}
                  </strong>

                  <span>
                    Total Applications
                  </span>

                </div>

              </div>

            </div>


            {/* FOOTER */}

            <div className="drawer-footer">

              <button
                className="secondary-drawer-btn"
                onClick={closeViewDrawer}
              >
                Close
              </button>

              <button
                className="primary-drawer-btn"
                onClick={() => {

                  closeViewDrawer();

                  setTimeout(() => {
                    handleEditJob(
                      selectedJob
                    );
                  }, 250);

                }}
              >

                <Pencil size={15} />

                Update Job

              </button>

            </div>

          </div>

        </div>

      )}


      {/* =====================================================
          RIGHT SIDE EDIT DRAWER
      ===================================================== */}

      {showEditDrawer && editJob && (

        <div
          className="drawer-overlay"
          onClick={closeEditDrawer}
        >

          <div
            className="job-drawer edit-drawer"
            onClick={(e) =>
              e.stopPropagation()
            }
          >

            {/* EDIT HEADER */}

            <div className="drawer-header">

              <div>

                <span className="drawer-label">
                  UPDATE JOB
                </span>

                <h2>
                  {editJob.jobTitle ||
                    "Update Job"}
                </h2>

                <p>
                  Job ID:{" "}
                  {editJob.jobId}
                </p>

              </div>


              <button
                className="drawer-close"
                onClick={closeEditDrawer}
                disabled={savingJob}
              >

                <X size={19} />

              </button>

            </div>


            {/* EDIT BODY */}

            <div className="drawer-body">

              {saveMessage && (

                <div className="success-message">

                  <CheckCircle2
                    size={17}
                  />

                  {saveMessage}

                </div>

              )}


              {saveError && (

                <div className="save-error">

                  <AlertCircle
                    size={17}
                  />

                  {saveError}

                </div>

              )}


              <div className="edit-form">

                {/* JOB TITLE */}

                <FormField
                  label="Job Title"
                  value={
                    editJob.jobTitle || ""
                  }
                  onChange={(value) =>
                    handleEditChange(
                      "jobTitle",
                      value
                    )
                  }
                />


                {/* TECHNOLOGY */}

                <FormField
                  label="Technology"
                  value={
                    editJob.technology || ""
                  }
                  onChange={(value) =>
                    handleEditChange(
                      "technology",
                      value
                    )
                  }
                />


                {/* SKILLS */}

                <FormField
                  label="Skills Required"
                  value={
                    editJob.skillsRequired || ""
                  }
                  onChange={(value) =>
                    handleEditChange(
                      "skillsRequired",
                      value
                    )
                  }
                />


                {/* LOCATION */}

                <FormField
                  label="Location"
                  value={
                    editJob.location || ""
                  }
                  onChange={(value) =>
                    handleEditChange(
                      "location",
                      value
                    )
                  }
                />


                <div className="form-two-column">

                  {/* EMPLOYMENT */}

                  <FormField
                    label="Employment Type"
                    value={
                      editJob.employeeType ||
                      ""
                    }
                    onChange={(value) =>
                      handleEditChange(
                        "employeeType",
                        value
                      )
                    }
                  />


                  {/* EXPERIENCE */}

                  <FormField
                    label="Experience Required"
                    value={
                      editJob.experienceRequired ||
                      ""
                    }
                    onChange={(value) =>
                      handleEditChange(
                        "experienceRequired",
                        value
                      )
                    }
                  />

                </div>


                <div className="form-two-column">

                  {/* VACANCY */}

                  <FormField
                    label="Vacancy"
                    type="number"
                    value={
                      editJob.vacancy ?? ""
                    }
                    onChange={(value) =>
                      handleEditChange(
                        "vacancy",
                        value
                      )
                    }
                  />


                  {/* SALARY */}

                  <FormField
                    label="Salary / Rate"
                    value={
                      editJob.salary ||
                      editJob.salaryRange ||
                      editJob.rate ||
                      ""
                    }
                    onChange={(value) =>
                      handleEditChange(
                        "salary",
                        value
                      )
                    }
                  />

                </div>


                {/* WORK MODE */}

                <FormField
                  label="Work Mode"
                  value={
                    editJob.workMode ||
                    editJob.mode ||
                    ""
                  }
                  onChange={(value) =>
                    handleEditChange(
                      "workMode",
                      value
                    )
                  }
                />


                {/* START DATE */}

                <FormField
                  label="Start Date"
                  type="date"
                  value={
                    editJob.startDate
                      ? String(
                          editJob.startDate
                        ).substring(0, 10)
                      : ""
                  }
                  onChange={(value) =>
                    handleEditChange(
                      "startDate",
                      value
                    )
                  }
                />


                {/* DESCRIPTION */}

                <div className="form-group">

                  <label>
                    Job Description
                  </label>

                  <textarea
                    rows="5"
                    value={
                      editJob.description ||
                      editJob.jobDescription ||
                      ""
                    }
                    onChange={(e) =>
                      handleEditChange(
                        "description",
                        e.target.value
                      )
                    }
                    placeholder="Enter job description"
                  />

                </div>


                {/* REQUIREMENTS */}

                <div className="form-group">

                  <label>
                    Requirements
                  </label>

                  <textarea
                    rows="5"
                    value={
                      editJob.requirements ||
                      editJob.jobRequirements ||
                      ""
                    }
                    onChange={(e) =>
                      handleEditChange(
                        "requirements",
                        e.target.value
                      )
                    }
                    placeholder="Enter job requirements"
                  />

                </div>


                {/* STATUS */}

                <div className="form-group">

                  <label>
                    Status
                  </label>

                  <select
                    value={
                      editJob.status ||
                      "PENDING"
                    }
                    onChange={(e) =>
                      handleEditChange(
                        "status",
                        e.target.value
                      )
                    }
                  >

                    <option value="PENDING">
                      PENDING
                    </option>

                    <option value="APPROVED">
                      APPROVED
                    </option>

                    <option value="REJECTED">
                      REJECTED
                    </option>

                    <option value="CLOSED">
                      CLOSED
                    </option>

                  </select>

                </div>

              </div>

            </div>


            {/* EDIT FOOTER */}

            <div className="drawer-footer">

              <button
                className="secondary-drawer-btn"
                onClick={closeEditDrawer}
                disabled={savingJob}
              >
                Cancel
              </button>


              <button
                className="primary-drawer-btn"
                onClick={handleUpdateJob}
                disabled={savingJob}
              >

                {savingJob ? (

                  <>
                    <RefreshCw
                      size={15}
                      className="refresh-spin"
                    />

                    Saving...

                  </>

                ) : (

                  <>
                    <Save size={15} />

                    Save Changes

                  </>

                )}

              </button>

            </div>

          </div>

        </div>

      )}


      <style>
        {tableCSS}
      </style>

    </Page>

  );

}


// =========================================================
// INFO BOX
// =========================================================

const InfoBox = ({
  icon,
  label,
  value
}) => (

  <div className="info-box">

    <div className="info-box-icon">
      {icon}
    </div>

    <div>

      <span>
        {label}
      </span>

      <strong>
        {
          value === null ||
          value === undefined ||
          value === ""
            ? "-"
            : value
        }
      </strong>

    </div>

  </div>

);


// =========================================================
// DETAIL SECTION
// =========================================================

const DetailSection = ({
  title,
  icon,
  children
}) => (

  <div className="detail-section">

    <div className="detail-section-title">

      <span className="detail-section-icon">
        {icon}
      </span>

      <h3>
        {title}
      </h3>

    </div>

    <div className="detail-section-content">

      {children}

    </div>

  </div>

);


// =========================================================
// DETAIL ROW
// =========================================================

const DetailRow = ({
  label,
  value
}) => (

  <div className="detail-row">

    <span className="detail-label">
      {label}
    </span>

    <span className="detail-value">

      {
        value === null ||
        value === undefined ||
        value === ""
          ? "-"
          : value
      }

    </span>

  </div>

);


// =========================================================
// FORM FIELD
// =========================================================

const FormField = ({
  label,
  value,
  onChange,
  type = "text"
}) => (

  <div className="form-group">

    <label>
      {label}
    </label>

    <input
      type={type}
      value={value ?? ""}
      onChange={(e) =>
        onChange(e.target.value)
      }
      placeholder={`Enter ${label.toLowerCase()}`}
    />

  </div>

);


// =========================================================
// PAGE COMPONENT
// =========================================================

const Page = ({
  title,
  children
}) => (

  <div className="my-jobs-page">

    <div className="my-jobs-title">

      <h1>
        {title}
      </h1>

    </div>

    <div className="my-jobs-card">

      {children}

    </div>

  </div>

);


// =========================================================
// CSS
// =========================================================

const tableCSS = `

.my-jobs-page {
  width: 100%;
  min-height: 100vh;
  padding: 25px;
  background: #f4f7fb;
  box-sizing: border-box;
}

.my-jobs-title {
  margin-bottom: 18px;
}

.my-jobs-title h1 {
  margin: 0;
  color: #0f172a;
  font-size: 24px;
  font-weight: 700;
}

.my-jobs-card {
  width: 100%;
  background: #ffffff;
  padding: 22px;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,.06);
  box-sizing: border-box;
}


/* HEADER */

.jobs-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 20px;
}

.jobs-header h2 {
  margin: 0 0 4px;
  font-size: 19px;
  color: #0f172a;
}

.jobs-header p {
  margin: 0;
  color: #64748b;
  font-size: 13px;
}


/* REFRESH */

.refresh-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid #dbe3ef;
  border-radius: 7px;
  background: #ffffff;
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.refresh-btn:hover {
  background: #eff6ff;
  border-color: #93c5fd;
}

.refresh-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}


/* TABLE */

.jobs-table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.jobs-table {
  width: 100%;
  min-width: 1100px;
  border-collapse: collapse;
  background: #ffffff;
}

.jobs-table th {
  padding: 12px 13px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  text-align: left;
  white-space: nowrap;
}

.jobs-table td {
  padding: 13px;
  border-bottom: 1px solid #edf2f7;
  color: #334155;
  font-size: 12px;
  vertical-align: middle;
}

.jobs-table tbody tr:hover {
  background: #f8fafc;
}


/* JOB */

.job-title-cell {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 190px;
}

.job-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 7px;
  background: #eff6ff;
  color: #2563eb;
}

.job-title-cell strong {
  display: block;
  color: #1e293b;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}

.job-title-cell small {
  display: block;
  margin-top: 3px;
  color: #94a3b8;
  font-size: 10px;
}


/* SKILLS */

.skills-cell {
  max-width: 220px;
  line-height: 1.5;
}


/* LOCATION */

.location-cell {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}


/* EXPERIENCE */

.experience-cell {
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}


/* VACANCY */

.vacancy-cell {
  display: flex;
  align-items: center;
  gap: 5px;
}


/* APPLICATION */

.application-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 27px;
  padding: 0 8px;
  border-radius: 7px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 11px;
  font-weight: 800;
}


/* STATUS */

.job-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 105px;
  height: 31px;
  padding: 0 8px;
  border-radius: 20px;
  border: 1px solid;
  font-size: 10px;
  font-weight: 800;
  outline: none;
  cursor: pointer;
  background: #ffffff;
}

.job-status:disabled {
  opacity: .6;
  cursor: not-allowed;
}

.job-status.pending {
  color: #b45309;
  background: #fffbeb;
  border-color: #fcd34d;
}

.job-status.approved {
  color: #15803d;
  background: #f0fdf4;
  border-color: #86efac;
}

.job-status.rejected {
  color: #dc2626;
  background: #fef2f2;
  border-color: #fca5a5;
}

.job-status.closed {
  color: #475569;
  background: #f1f5f9;
  border-color: #cbd5e1;
}


/* ACTIONS */

.job-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.job-action-btn {
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 7px;
  border: 1px solid #dbe3ef;
  background: #ffffff;
  cursor: pointer;
  transition: all .2s ease;
}

.job-action-btn:hover {
  transform: translateY(-1px);
}

.job-action-btn.view {
  color: #2563eb;
}

.job-action-btn.view:hover {
  background: #eff6ff;
  border-color: #93c5fd;
}

.job-action-btn.edit {
  color: #d97706;
}

.job-action-btn.edit:hover {
  background: #fffbeb;
  border-color: #fcd34d;
}


/* DRAWER OVERLAY */

.drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(15, 23, 42, .38);
  display: flex;
  justify-content: flex-end;
  animation: drawerOverlay .2s ease;
}


/* RIGHT DRAWER */

.job-drawer {
  width: min(420px, 94vw);
  height: 100vh;
  background: #ffffff;
  box-shadow: -10px 0 35px rgba(15, 23, 42, .18);
  display: flex;
  flex-direction: column;
  animation: drawerSlide .25s ease;
}

.edit-drawer {
  width: min(450px, 94vw);
}


/* DRAWER HEADER */

.drawer-header {
  min-height: 88px;
  padding: 20px 22px;
  display: flex;
  justify-content: space-between;
  gap: 15px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  box-sizing: border-box;
}

.drawer-label {
  display: block;
  margin-bottom: 5px;
  color: #2563eb;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: .8px;
}

.drawer-header h2 {
  margin: 0;
  color: #0f172a;
  font-size: 19px;
  font-weight: 750;
}

.drawer-header p {
  margin: 4px 0 0;
  color: #94a3b8;
  font-size: 11px;
}

.drawer-close {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #ffffff;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.drawer-close:hover {
  background: #f8fafc;
  color: #0f172a;
}


/* DRAWER BODY */

.drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 22px;
  box-sizing: border-box;
}

.drawer-body::-webkit-scrollbar {
  width: 5px;
}

.drawer-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}


/* STATUS */

.drawer-status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  padding: 12px 14px;
  border-radius: 9px;
  background: #f8fafc;
}

.drawer-status-row span {
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}


/* QUICK INFO */

.quick-info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin-bottom: 22px;
}

.info-box {
  min-height: 66px;
  padding: 11px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #e5e7eb;
  border-radius: 9px;
  background: #ffffff;
  box-sizing: border-box;
}

.info-box-icon {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: #eff6ff;
  color: #2563eb;
}

.info-box span {
  display: block;
  color: #94a3b8;
  font-size: 10px;
  margin-bottom: 3px;
}

.info-box strong {
  display: block;
  color: #1e293b;
  font-size: 12px;
  font-weight: 700;
}


/* DETAIL SECTION */

.detail-section {
  margin-bottom: 20px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
}

.detail-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 13px;
  padding-bottom: 10px;
  border-bottom: 1px solid #edf2f7;
}

.detail-section-title h3 {
  margin: 0;
  color: #1e293b;
  font-size: 13px;
  font-weight: 750;
}

.detail-section-icon {
  color: #2563eb;
  display: flex;
}


/* DETAIL ROW */

.detail-row {
  display: grid;
  grid-template-columns: 150px 1fr;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px dashed #e5e7eb;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: #94a3b8;
  font-size: 11px;
  font-weight: 600;
}

.detail-value {
  color: #334155;
  font-size: 12px;
  line-height: 1.5;
  word-break: break-word;
}


/* DESCRIPTION */

.description-box {
  color: #475569;
  font-size: 12px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}


/* APPLICATION SUMMARY */

.application-summary {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 15px;
  margin-bottom: 5px;
  border-radius: 10px;
  background: #eff6ff;
  border: 1px solid #dbeafe;
}

.application-summary-icon {
  width: 38px;
  height: 38px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2563eb;
  color: #ffffff;
}

.application-summary strong {
  display: block;
  color: #1d4ed8;
  font-size: 18px;
}

.application-summary span {
  display: block;
  color: #64748b;
  font-size: 11px;
}


/* EDIT FORM */

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.form-two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  color: #334155;
  font-size: 11px;
  font-weight: 700;
}

.form-group input,
.form-group select,
.form-group textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #ffffff;
  color: #1e293b;
  padding: 10px 11px;
  font-size: 12px;
  outline: none;
  font-family: inherit;
}

.form-group input,
.form-group select {
  height: 38px;
}

.form-group textarea {
  resize: vertical;
  min-height: 100px;
  line-height: 1.5;
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  border-color: #60a5fa;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, .08);
}


/* MESSAGES */

.success-message,
.save-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 13px;
  border-radius: 8px;
  margin-bottom: 15px;
  font-size: 12px;
  font-weight: 600;
}

.success-message {
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.save-error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}


/* DRAWER FOOTER */

.drawer-footer {
  min-height: 70px;
  padding: 14px 22px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 9px;
  box-sizing: border-box;
}

.secondary-drawer-btn,
.primary-drawer-btn {
  height: 36px;
  padding: 0 14px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.secondary-drawer-btn {
  border: 1px solid #dbe3ef;
  background: #ffffff;
  color: #475569;
}

.secondary-drawer-btn:hover {
  background: #f8fafc;
}

.primary-drawer-btn {
  border: 1px solid #2563eb;
  background: #2563eb;
  color: #ffffff;
}

.primary-drawer-btn:hover {
  background: #1d4ed8;
}

.primary-drawer-btn:disabled,
.secondary-drawer-btn:disabled {
  opacity: .6;
  cursor: not-allowed;
}


/* LOADING */

.jobs-loading {
  min-height: 250px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #64748b;
}

.jobs-loading p {
  margin-top: 10px;
  font-size: 13px;
}


/* EMPTY */

.jobs-empty {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  text-align: center;
}

.jobs-empty h3 {
  margin: 12px 0 5px;
  color: #334155;
  font-size: 17px;
}

.jobs-empty p {
  margin: 0;
  font-size: 13px;
}


/* ERROR */

.jobs-error {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 15px;
  padding: 12px 15px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
}

.jobs-error button {
  border: none;
  background: transparent;
  color: #2563eb;
  font-weight: 600;
  cursor: pointer;
}


/* ANIMATION */

.refresh-spin {
  animation: refreshSpin 1s linear infinite;
}

@keyframes refreshSpin {

  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }

}

@keyframes drawerSlide {

  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0);
  }

}

@keyframes drawerOverlay {

  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }

}


/* RESPONSIVE */

@media (max-width: 768px) {

  .my-jobs-page {
    padding: 15px;
  }

  .my-jobs-card {
    padding: 15px;
  }

  .jobs-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .job-drawer,
  .edit-drawer {
    width: 100%;
  }

  .quick-info-grid {
    grid-template-columns: 1fr;
  }

  .form-two-column {
    grid-template-columns: 1fr;
  }

  .detail-row {
    grid-template-columns: 1fr;
    gap: 4px;
  }

}

`;

export default AllJob;